import { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { getJwtToken } = useAuth();
  // Default to showing admin pages — hide only if confirmed non-admin
  const [isAdmin, setIsAdmin] = useState(true);

  const checkAdmin = useCallback(async () => {
    try {
      const token = await getJwtToken();
      if (!token) return;
      const API_BASE = import.meta.env.VITE_API_URL ?? '';

      // Try the /me endpoint first
      const res = await fetch(`${API_BASE}/api/mgmt/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const role = data?.user?.role ?? data?.profile?.role ?? data?.role ?? '';
        if (role) {
          setIsAdmin(role === 'admin');
          return;
        }
      }

      // Fallback: try an admin-only endpoint. If it returns 403, user is not admin.
      const adminCheck = await fetch(`${API_BASE}/api/mgmt/users?limit=1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsAdmin(adminCheck.ok);
    } catch {
      // Keep default (admin) on error
    }
  }, [getJwtToken]);

  useEffect(() => {
    checkAdmin();
  }, [checkAdmin]);

  const handleClick = () => {
    if (onClose) onClose();
  };

  const link = (to: string, icon: string, label: string, end?: boolean) => (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `sidebar__link${isActive ? ' sidebar__link--active' : ''}`
      }
      onClick={handleClick}
    >
      <span className="sidebar__icon" dangerouslySetInnerHTML={{ __html: icon }} />
      <span className="sidebar__label">{label}</span>
    </NavLink>
  );

  return (
    <aside className={`sidebar${open ? ' sidebar--open' : ''}`}>
      <nav className="sidebar__nav">
        {/* Available to all users */}
        {link('/admin/setup', '&#x1F680;', 'Get Started')}
        {link('/admin', '&#x2302;', 'Dashboard', true)}

        {/* Admin only */}
        {isAdmin && (
          <>
            {link('/admin/users', '&#x2603;', 'Users')}
            {link('/admin/teams', '&#x2318;', 'Teams')}
            {link('/admin/projects', '&#x25A3;', 'Projects')}
            {link('/admin/models', '&#x2B22;', 'Models')}
            {link('/admin/accounts', '&#x2601;', 'Accounts')}
            {link('/admin/ip', '&#x2699;', 'IP Allow-list')}
            {link('/admin/usage', '&#x2261;', 'Usage')}
            {link('/admin/metrics', '&#x2237;', 'Bedrock Metrics')}
            {link('/admin/requests', '&#x2709;', 'Access Requests')}
            {link('/admin/audit', '&#x2638;', 'Audit Log')}
            {link('/admin/cache', '&#x29C9;', 'Cache')}
            {link('/admin/reports', '&#x2637;', 'Reports')}
          </>
        )}

        {/* Available to all users */}
        {link('/admin/status', '&#x2665;', 'System Status')}

        <div className="sidebar__divider" />

        {link('/admin/me', '&#x2630;', 'My Profile')}
      </nav>
    </aside>
  );
}
